// Mobile port of the web Task Configuration → ETC Intervals editor. Reads/
// writes user_settings.etc_intervals directly (see src/interfaces/
// settings.ts ETCInterval on web) — same shape, same source of truth as the
// web app. Unlike web, inline edits to existing labels actually persist.
import React, { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { makeThemedStyles } from '../../v10/theme';
import { SectionTitle, Field, GoldButton } from '../../v10/ui';
import { useSettingColumn } from './useSettingColumn';

interface ETCInterval {
  id: string;
  label: string;
}

export default function EtcIntervalsEditor({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const { value: intervals, setValue: setIntervals, loading } = useSettingColumn<ETCInterval[]>(
    userId,
    'etc_intervals',
    []
  );
  const [newLabel, setNewLabel] = useState('');

  const updateLabel = (id: string, label: string) => {
    setIntervals(intervals.map((i) => (i.id === id ? { ...i, label } : i)));
  };

  const deleteInterval = (id: string) => {
    setIntervals(intervals.filter((i) => i.id !== id));
  };

  const addInterval = () => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    const interval: ETCInterval = { id: `etc-${Date.now()}`, label: trimmed };
    setIntervals([...intervals, interval]);
    setNewLabel('');
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <Text style={styles.hint}>Loading…</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <SectionTitle style={styles.sectionGap}>ETC Intervals</SectionTitle>
      <Text style={styles.hint}>Estimated-time-to-complete options offered when sizing a task.</Text>

      {intervals.length === 0 ? <Text style={styles.hint}>No intervals yet.</Text> : null}

      {intervals.map((interval) => (
        <View key={interval.id} style={styles.row}>
          <Field style={styles.flex1}
            inputStyle={[styles.input, styles.flex1]}
            value={interval.label}
            onChangeText={(t) => updateLabel(interval.id, t)}
            placeholder="e.g. 15 min"
          />
          <Pressable style={styles.deleteBtn} onPress={() => deleteInterval(interval.id)}>
            <Text style={styles.deleteText}>✕</Text>
          </Pressable>
        </View>
      ))}

      <View style={styles.row}>
        <Field style={styles.flex1}
          inputStyle={[styles.input, styles.flex1]}
          value={newLabel}
          onChangeText={setNewLabel}
          placeholder="New interval label"
        />
        <GoldButton label="Add" onPress={addInterval} style={styles.actionGap} />
      </View>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
  actionGap: { marginTop: 8 },
  sectionGap: { marginTop: 18 },
  container: { gap: 8 },
  hint: { fontSize: 12, color: t.ivorySoft, marginTop: 6, fontStyle: 'italic' },
  row: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 },
  flex1: { flex: 1 },
  input: {
  },
  deleteBtn: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  deleteText: { color: t.lapsed, fontWeight: '900' },
}),
);
